import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminContactServices } from "../../services/admin/AdminContactServices";
import { useNotification } from "../../context/NotificationContext";
import { ChevronLeft, User, Mail, MessageSquare, Send, Loader2, Calendar, Reply } from "lucide-react";
import { useComingSoon } from "../../utils/useComingSoon";

export default function ContactDetailsPage() {
    const { id } = useParams();
    const { showNotification } = useNotification();
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [replyContent, setReplyContent] = useState("");
    const sending = false; // Setter only used in commented-out reply block
    const { showComingSoon } = useComingSoon();

    const fetchMessageDetails = useCallback(async () => {
        try {
            setLoading(true);
            const res = await AdminContactServices.getContactDetails(id);
            if (res.status === "success") {
                setMessage(res.data);
            }
        } catch (error) {
            console.error("Failed to fetch message:", error);
            showNotification({ type: "failed", message: "Failed to load message details" });
        } finally {
            setLoading(false);
        }
    }, [id, showNotification]);

    useEffect(() => {
        fetchMessageDetails();
    }, [fetchMessageDetails]);

    const handleSendReply = async (e) => {
        e.preventDefault();

        showComingSoon({
            title: "Reply Feature Coming Soon!",
            message: "The ability to send direct replies from the dashboard is currently in development and will be available in a future update.",
        });
        // NOTE: Reply functionality is temporarily disabled.
        // When re-enabling, remove the showComingSoon call above and restore the code below:
        /*
        if (!replyContent.trim()) return;
        try {
            setSending(true);
            const res = await AdminContactServices.replyToContact(id, replyContent);
            if (res.status === "success") {
                showNotification({ type: "success", message: "Reply sent and stored successfully!" });
                setReplyContent("");
                fetchMessageDetails();
            }
        } catch (error) {
            showNotification({ type: "failed", message: "Failed to send reply" });
        } finally {
            setSending(false);
        }
        */
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case "unread":
                return <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Unread</span>;
            case "read":
                return <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Read</span>;
            case "replied":
                return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Replied</span>;
            default:
                return <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">{status}</span>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="animate-spin text-jazz-brown w-12 h-12" />
            </div>
        );
    }

    if (!message) {
        return (
            <div className="p-6 text-center text-black dark:text-white/50">Message not found.</div>
        );
    }

    return (
        <div className="p-6 lg:p-10 max-w-5xl mx-auto min-h-screen bg-[#FAFAFA] dark:bg-[#111]">
            <Link to="/admin/contacts" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-black hover:text-black/70 dark:text-white/40 dark:hover:text-white transition-colors mb-8">
                <ChevronLeft size={16} /> Back to Messages
            </Link>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-serif text-black dark:text-white mb-2">{message.subject || "No Subject"}</h1>
                    <div className="flex items-center gap-4 text-sm text-black dark:text-white/50">
                        <span className="flex items-center gap-2 font-medium">
                            <Calendar size={16} />
                            {new Date(message.created_at).toLocaleString()}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Status</span>
                    {getStatusBadge(message.status)}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Message & Conversation */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Original Message */}
                    <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 shadow-sm">
                        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-black/5">
                            <div className="w-12 h-12 rounded-full bg-jazz-brown/10 flex items-center justify-center text-jazz-brown">
                                <User size={24} />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-black dark:text-white">{message.name}</h3>
                                <p className="text-sm text-black dark:text-white/50">{message.email}</p>
                            </div>
                        </div>
                        <div className="prose dark:prose-invert max-w-none">
                            <p className="text-black dark:text-white/80 leading-relaxed whitespace-pre-wrap">
                                {message.message}
                            </p>
                        </div>
                    </div>

                    {/* Existing Replies */}
                    {message.replies && message.replies.length > 0 && (
                        <div className="space-y-6">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-black dark:text-white/50 px-2 flex items-center gap-2">
                                <Reply size={16} /> Previous Replies
                            </h2>
                            {message.replies.map(reply => (
                                <div key={reply.id} className="bg-jazz-brown/[0.03] dark:bg-jazz-brown/[0.05] p-6 rounded-2xl border border-jazz-brown/10 shadow-sm ml-8">
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-jazz-brown uppercase tracking-wider">Replied by {reply.admin_name}</span>
                                        </div>
                                        <span className="text-xs text-black/60 dark:text-white/30 font-medium italic">
                                            {new Date(reply.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="text-sm text-black dark:text-white/80 leading-relaxed whitespace-pre-wrap">
                                        {reply.content}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Reply Form */}
                    <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 shadow-sm">
                        <h2 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <MessageSquare size={20} className="text-jazz-brown" />
                            Send Reply
                        </h2>
                        <form onSubmit={handleSendReply}>
                            <textarea
                                required
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                placeholder="Type your message to the user here..."
                                className="w-full h-40 p-4 rounded-xl border border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#222] text-sm focus:border-jazz-brown outline-none resize-none transition-all placeholder:italic"
                            />
                            <div className="mt-4 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={sending || !replyContent.trim()}
                                    className="inline-flex items-center gap-3 px-8 py-3.5 bg-black hover:bg-jazz-brown dark:bg-white dark:hover:bg-jazz-brown text-white dark:text-black hover:text-white rounded-xl transition-all font-bold uppercase tracking-widest text-xs disabled:opacity-50 disabled:cursor-not-allowed shadow-lg active:scale-95"
                                >
                                    {sending ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={16} />
                                            Send Reply
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Right: Info Sidebar */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-black/5 shadow-sm sticky top-10">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-black dark:text-white/50 mb-6" style={{ fontFamily: "Playfair Display, serif" }}>Contact Information</h2>
                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 w-8 h-8 rounded-lg bg-black/[0.03] dark:bg-white/5 flex items-center justify-center text-black/70 dark:text-white/40">
                                    <User size={16} />
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/40 mb-0.5">Full Name</p>
                                    <p className="text-sm font-medium text-black dark:text-white">{message.name}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 w-8 h-8 rounded-lg bg-black/[0.03] dark:bg-white/5 flex items-center justify-center text-black/70 dark:text-white/40">
                                    <Mail size={16} />
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/40 mb-0.5">Email Address</p>
                                    <a href={`mailto:${message.email}`} className="text-sm font-medium text-jazz-brown hover:underline truncate block">
                                        {message.email}
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 w-8 h-8 rounded-lg bg-black/[0.03] dark:bg-white/5 flex items-center justify-center text-black/70 dark:text-green-500 text-green-600">
                                    <MessageSquare size={16} />
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-black dark:text-white/40 mb-0.5">Total Interaction</p>
                                    <p className="text-sm font-medium text-black dark:text-white">{message.replies?.length || 0} Replies sent</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
